import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PDFReader.css';
import Loading from '../../Common/Loading';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const PAGE_HEIGHT = 842; // standard A4 height in px, adjust if needed
const BUFFER = 2; // number of pages to render above and below viewport

const PDFReader = (props) => {
    const { videoUrl, isDownloadable } = props;
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [inputValue, setInputValue] = useState(1);
    const [visiblePages, setVisiblePages] = useState(new Set([0, 1, 2]));
    const pageRefs = useRef([]);
    const containerRef = useRef();

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    function onLoadError(error) {
        console.error('PDF Load Error:', error);
    }

    const isScrollingProgrammatically = useRef(false);

    function scrollToPage(pageno) {
        setPageNumber(pageno);
        setInputValue(pageno);
        isScrollingProgrammatically.current = true;
        pageRefs.current[pageno - 1]?.scrollIntoView();
        isScrollingProgrammatically.current = false;
    }

    function previousPage() {
        scrollToPage(pageNumber - 1);
    }

    function nextPage() {
        scrollToPage(pageNumber + 1);
    }

    // Observe which pages are visible and pre-render BUFFER pages around them
    useEffect(() => {
        if (!numPages) return;

        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const pageIndex = Math.floor(scrollTop / PAGE_HEIGHT);
            const currentPage = Math.min(Math.max(pageIndex + 1, 1), numPages);
            setPageNumber(currentPage);
            setInputValue(currentPage);

            // Pre-render buffer pages around current
            setVisiblePages((prev) => {
                const next = new Set(prev);
                for (let i = Math.max(0, pageIndex - BUFFER); i <= Math.min(numPages - 1, pageIndex + BUFFER); i++) {
                    next.add(i);
                }
                return next;
            });
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [numPages]);

    if (!videoUrl) {
        return (
            <div className="pdf_reader pdf_reader--error">
                <h2>Content Unavailable</h2>
                <p>The PDF document you're trying to view is currently unavailable.</p>
            </div>
        );
    }

    return (
        <div className="pdf_reader">
            <div className="pdf_reader_document" ref={containerRef}>
                <Document
                    file={encodeURI(process.env.REACT_APP_CONTENT_URL + videoUrl)}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onLoadError}
                    loading={<Loading type="text" />}
                >
                    {Array.from({ length: numPages }, (_, i) => (
                        <div
                            key={`page_${i + 1}`}
                            ref={(el) => (pageRefs.current[i] = el)}
                            data-page-index={i}
                            style={{ minHeight: PAGE_HEIGHT }}
                        >
                            {visiblePages.has(i) ? (
                                <Page
                                    pageNumber={i + 1}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                    width={containerRef.current?.clientWidth || undefined}
                                />
                            ) : null}
                        </div>
                    ))}
                </Document>
            </div>

            <p className="pdf_reader_page_number">
                Page {pageNumber} of {numPages}
            </p>

            <div className="buttonc pdf_reader_menu">
                <button type="button" className="btn btn_secondary" disabled={pageNumber <= 1} onClick={previousPage}>
                    Previous
                </button>
                <input
                    type="number"
                    min={1}
                    max={numPages}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val >= 1 && val <= numPages) scrollToPage(val);
                        else setInputValue(pageNumber);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const val = Number(e.target.value);
                            if (val >= 1 && val <= numPages) scrollToPage(val);
                            else setInputValue(pageNumber);
                        }
                    }}
                    className="pdf_reader_page_input"
                />
                <button
                    type="button"
                    className="btn btn_primary pdf-btn-secondary"
                    disabled={pageNumber >= numPages}
                    onClick={nextPage}
                >
                    Next
                </button>
                {isDownloadable ? (
                    <button
                        type="button"
                        className="btn btn_secondary ml-3 pdf_reader_menu_download"
                        onClick={async () => {
                            const response = await fetch(process.env.REACT_APP_CONTENT_URL + videoUrl);
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = videoUrl.split('/').pop();
                            a.click();
                            window.URL.revokeObjectURL(url);
                        }}
                    >
                        Download
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default PDFReader;
